/**
 * JobCard Component
 *
 * Draggable card representing a single job assignment
 *
 * Features:
 * - Draggable via @dnd-kit
 * - Shows job summary info
 * - Priority badge
 * - Status indicator
 * - Quick actions (unassign)
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, MapPin, Briefcase, X } from 'lucide-react';
import type { JobAssignment } from '../types/dispatch.types';
import { format } from 'date-fns';

interface JobCardProps {
  assignment: JobAssignment;
  onUnassign: () => void;
  onClick?: () => void;
}

export function JobCard({ assignment, onUnassign, onClick }: JobCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: assignment.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStatusColor = (status: JobAssignment['status']) => {
    switch (status) {
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: number) => {
    if (priority >= 75) return 'bg-red-100 text-red-800';
    if (priority >= 50) return 'bg-orange-100 text-orange-800';
    if (priority >= 25) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  const formatTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'h:mm a');
    } catch {
      return 'Invalid time';
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'shadow-lg' : ''}`}
      {...attributes}
      {...listeners}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header with Priority and Status */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {assignment.priority > 0 && (
              <Badge variant="outline" className={getPriorityColor(assignment.priority)}>
                Priority: {assignment.priority}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={getStatusColor(assignment.status)}>
              {assignment.status.replace('_', ' ')}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onUnassign();
              }}
              className="h-6 w-6 p-0 hover:bg-red-100"
              aria-label="Unassign job"
            >
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        </div>

        {/* Customer Name */}
        <div
          onClick={onClick}
          className="cursor-pointer hover:text-blue-600 transition-colors"
        >
          <h4 className="font-semibold text-sm truncate" title={assignment.job.customerName}>
            {assignment.job.customerName}
          </h4>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span className="truncate" title={assignment.job.address}>
            {assignment.job.address}
          </span>
        </div>

        {/* Job Type */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Briefcase className="h-4 w-4 flex-shrink-0" />
          <span>{assignment.job.jobType}</span>
        </div>

        {/* Estimated Start Time */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>Est: {formatTime(assignment.estimatedStartTime)}</span>
        </div>

        {/* Scheduled Date */}
        <div className="text-xs text-gray-500">
          Scheduled: {format(new Date(assignment.job.scheduledDate), 'MMM d, yyyy')}
        </div>
      </CardContent>
    </Card>
  );
}
